import { MdClose } from "react-icons/md";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";

import {
  AlertDialog,
  AlertDialogContent,
} from "@/components/ui/alert-dialog/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog/dialog";
import { useSalaryModal } from "./use-salary-modal";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form/form";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { SalaryComponents } from "./salary-components";
import { editAndApproveSalary } from "@/config/mutation/admin";
import { Button } from "@/components/ui/button/button";

const formSchema = z.object({
  approved: z.boolean().default(false).optional(),
  components: z
    .array(
      z.object({
        type: z.string().optional(),
        name: z.string().optional(),
        amount: z.coerce.number().optional(),
        description: z.string().optional(),
      })
    )
    .optional(),
});

export type FormValues = z.infer<typeof formSchema>;

export const SalaryModal = () => {
  const queryClient = useQueryClient();
  const user = Cookies.get("key") as string;
  const { open, onOpenChange, salary } = useSalaryModal();
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      approved: false,
      components: [],
    },
  });

  const { mutate, isPending: isSubmitting } = useMutation({
    mutationFn: editAndApproveSalary(user),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.

    const realData = {
      salaryId: salary?.id as string,
      postData: {
        approved: values.approved,
        components: values.components,
      },
    };

    // console.log(realData);

    mutate(realData, {
      onSuccess: async () => {
        // console.log("New Employee", data);
        await queryClient.invalidateQueries({
          queryKey: ["employee-salary"],
        });

        setIsSubmitDialogOpen(false);
        toast.success("Salary Updated");
        onOpenChange(false);

        form.reset();
      },

      onError: (error) => {
        console.log("ERROR WHILE updating salary", error);
        const isAxiosError = error instanceof AxiosError;
        if (isAxiosError) {
          const errorReason = error?.response?.data?.error as string;
          toast.error(errorReason);
        } else {
          toast.error(error?.message);
        }
      },
    });
  }

  useEffect(() => {
    if (salary) {
      form.setValue("approved", salary?.approved);
      form.setValue("components", salary?.components);
    }
  }, [salary]);

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="p-0 max-w-[1400px]">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="border-b px-3 py-2 flex items-center justify-between">
                <h3 className="text-xl font-medium tracking-normal">Salary</h3>
                <div className="relative flex items-center gap-4">
                  <Dialog
                    open={isSubmitDialogOpen}
                    onOpenChange={setIsSubmitDialogOpen}
                  >
                    <DialogTrigger>
                      <Button type="button" size="sm" disabled={isSubmitting}>
                        Update
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl">
                      <DialogHeader>
                        <DialogTitle>Are you absolutely sure?</DialogTitle>
                      </DialogHeader>

                      <FormField
                        control={form.control}
                        name="approved"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>Salary Approved?</FormLabel>
                              <FormDescription>
                                If approved, then this action will create
                                permanent salary for corresponding month for
                                employee.
                              </FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />

                      <div className="flex items-center justify-end">
                        <Button
                          type="submit"
                          size="sm"
                          disabled={isSubmitting}
                          onClick={() => {
                            form.handleSubmit(onSubmit)();
                          }}
                        >
                          Continue
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <button
                    type="button"
                    className="p-1 rounded-md transition-all duration-300 hover:bg-accent"
                    onClick={() => {
                      onOpenChange(false);
                      // form.reset();
                    }}
                  >
                    <MdClose className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="flex">
                <div className="flex-[0.3] p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <p>Salary Requested by : </p>
                    <p> {salary?.employee?.name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p>Total Earnings : </p>
                    <p> {salary?.totalEarnings}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p>Total Deductions : </p>
                    <p> {salary?.totalDeductions}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p>Net Pay : </p>
                    <p> {salary?.netPay}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p>Period : </p>
                    <p> {salary?.period}</p>
                  </div>
                </div>
                <div className="flex-[0.7] p-5 space-y-3 border-l max-h-[700px] overflow-y-auto">
                  <SalaryComponents />
                </div>
              </div>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
